import { Component, signal, computed, model, effect, untracked, OnInit, input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ILocationForNOA, ICargo, CargoHelper } from '@pmis3/master-data-core';
import { KENDO_LABELS } from "@progress/kendo-angular-label";
import { KENDO_TEXTBOX, KENDO_INPUTS } from "@progress/kendo-angular-inputs";
import { KENDO_DATEINPUTS } from '@progress/kendo-angular-dateinputs';
import { IconsModule } from '@progress/kendo-angular-icons';
import { KENDO_BUTTONS } from "@progress/kendo-angular-buttons";
import { IVesselMovement, VesselStopTypeForStepper, IVesselStop, selectEditingVesselActivities, VesselActivityHelper, UpdateVesselActivitiesBeingEdited, selectVesselActivityPurposes, selectSelectedVesselVisit, IVesselActivityPurpose, selectLadenStatuses, selectLocationsForNOA, selectVesselOrientation, selectTerminalsForNOA, IServiceApplication, UpdateSelectedVesselActivitiesIndices, selectCargoTypes, selectCargoUnitTypes, selectTransitStatuses, selectSelectedVesselActivitiesIndices } from '@pmis3/vessel-activity-core';
import { IVesselActivity, VesselActivityType, VesselActivityIndicesAndStopType } from '@pmis3/vessel-activity-core';
import { VesselVisitDetailMovementServiceApplicationPickerComponent } from './vessel-visit-detail-movement-service-application-picker/vessel-visit-detail-movement-service-application-picker.component';
import { KENDO_DROPDOWNS } from "@progress/kendo-angular-dropdowns";
import { FormsModule } from '@angular/forms';
import { VesselVisitDetailOrganizationComponent } from '../vessel-visit-detail-organization/vessel-visit-detail-organization.component';
import { Store } from '@ngrx/store';
import { VesselVisitDetailMovementCargoComponent } from './vessel-visit-detail-movement-cargo/vessel-visit-detail-movement-cargo.component';
import { VesselVisitDetailTransitStatusPickerComponent } from './vessel-visit-detail-transit-status-picker/vessel-visit-detail-transit-status-picker.component';
import { VesselVisitDetailMovementAuthorityStatusComponent } from './vessel-visit-detail-movement-authority-status/vessel-visit-detail-movement-authority-status.component';

@Component({
    selector: 'pmis3-vessel-visit-detail-movement',
    standalone: true,
    imports: [
        CommonModule,
        KENDO_LABELS,
        KENDO_TEXTBOX,
        KENDO_INPUTS,
        KENDO_DATEINPUTS,
        IconsModule,
        KENDO_BUTTONS,
        VesselVisitDetailMovementServiceApplicationPickerComponent,
        KENDO_DROPDOWNS,
        FormsModule,
        VesselVisitDetailMovementCargoComponent,
        VesselVisitDetailTransitStatusPickerComponent,
        VesselVisitDetailMovementAuthorityStatusComponent
    ],
    templateUrl: 'vessel-visit-detail-movement.component.html',
    styleUrl: 'vessel-visit-detail-movement.component.scss'
})
export class VesselVisitDetailMovementComponent {
    @Output() allFieldsValidChange = new EventEmitter<boolean>();

    typedIndicesForEditingVesselActivities = this.store.selectSignal(selectSelectedVesselActivitiesIndices)
    vesselActivities = this.store.selectSignal(selectEditingVesselActivities);
    locations = this.store.selectSignal(selectLocationsForNOA);
    terminalsDropdown = this.store.selectSignal(selectTerminalsForNOA);
    vesselActivityPurposes = this.store.selectSignal(selectVesselActivityPurposes);
    vesselOrientations = this.store.selectSignal(selectVesselOrientation);
    vesselLadenStatus = this.store.selectSignal(selectLadenStatuses);
    vesselVisit = this.store.selectSignal(selectSelectedVesselVisit);
    cargoTypes = this.store.selectSignal(selectCargoTypes);
    cargoUnitTypes = this.store.selectSignal(selectCargoUnitTypes);
    statuses = this.store.selectSignal(selectTransitStatuses);

    state = computed(() => {
        const arrivalMovement = this.arrivalMovement();
        const departureMovement = this.departureMovement();

        const purposes = this.stop()?.purposes ?? [];
        const selectedPurposesId = purposes.flatMap(purpose => purpose.isDeleted != true ? purpose.purposeId : [])

        const stopLocation = this.stop()?.startLocation;
        const locations = this.locations();
        const terminals = this.terminalsDropdown();
        const stopLocationIsTerminalOrPilotStation = terminals.some(location => location.id == stopLocation?.id);

        const selectedTerminal = stopLocationIsTerminalOrPilotStation ? terminals.find(location => location.id == stopLocation?.id) : terminals.find(location => location.id == stopLocation?.parentId);
        const selectedBerth = stopLocationIsTerminalOrPilotStation ? undefined : locations.find(location => location.id == stopLocation?.id)
        return {
            selectedPurposesId: signal(selectedPurposesId),
            arrivalCargoes: signal(arrivalMovement?.cargoes ?? []),
            departureCargoes: signal(departureMovement?.cargoes ?? []),
            selectedTerminal: signal(selectedTerminal),
            selectedBerth: signal(selectedBerth)
        }
    })

    showCargoState = computed(() => {
        const _ = this.typedIndicesForEditingVesselActivities();
        return {
            showArrivalCargo: signal(false),
            showDepartureCargo: signal(false)
        }
    })

    arrivalButtonText = computed(() => this.showCargoState().showArrivalCargo() ? 'ARRIVAL' : 'CARGO')
    arrivalButtonDisabled = computed(() => !this.arrivalMovementIsValid() || !this.allArrivalCargoesValid())
    departureButtonText = computed(() => this.showCargoState().showDepartureCargo() ? 'DEPARTURE' : 'CARGO')
    departureButtonDisabled = computed(() => !this.departureMovementIsValid() || !this.allDepartureCargoesValid())

    allArrivalCargoesValid = signal(true);
    allDepartureCargoesValid = signal(true);

    arrivalMovementIndex = computed(() => this.typedIndicesForEditingVesselActivities()?.find(index => index.type == VesselStopTypeForStepper.Arrival)?.index);
    departureMovementIndex = computed(() => this.typedIndicesForEditingVesselActivities()?.find(index => index.type == VesselStopTypeForStepper.Departure)?.index); 
    stopIndex = computed(() => this.typedIndicesForEditingVesselActivities()?.find(index => index.type == VesselStopTypeForStepper.Shift)?.index);

    stop = computed(() => {
        const vesselActivities = this.vesselActivities();
        const stopIndex = this.stopIndex();
        if (!stopIndex || vesselActivities == undefined || vesselActivities.length < stopIndex) {
            return null;
        }
        return vesselActivities[stopIndex] as IVesselStop;
    })
    arrivalMovement = computed(() => {
        const vesselActivities = this.vesselActivities();
        const arrivalMovementIndex = this.arrivalMovementIndex()
        if (arrivalMovementIndex != undefined && vesselActivities && vesselActivities.length > arrivalMovementIndex) {
            return vesselActivities[arrivalMovementIndex] as IVesselMovement;
        }
        return null;
    })
    departureMovement = computed(() => {
        const vesselActivities = this.vesselActivities();
        const departureMovementIndex = this.departureMovementIndex();
        if (departureMovementIndex && vesselActivities && vesselActivities.length > departureMovementIndex) {
            return vesselActivities[departureMovementIndex] as IVesselMovement;
        }
        return null;
    })

    berthDropdown = computed(() => {
        const terminal = this.state().selectedTerminal();
        if (terminal) {
            return this.locations().filter(location => location.parentId == terminal.id)
        }
        return [];
    })

    estimatedStartTimeLabel = computed(() => `Planned Arrival (TO ${this.stop()?.startLocation?.shortCode})`);
    estimatedEndTimeLabel = computed(() => `Planned Departure (FROM ${this.stop()?.startLocation?.shortCode})`);
    startTimeLabel = computed(() => `Actual Arrival (TO ${this.stop()?.startLocation?.shortCode})`);
    endTimeLabel = computed(() => `Actual Departure (FROM ${this.stop()?.startLocation?.shortCode})`);

    startTime = computed(() => this.arrivalMovement()?.endTime ?? null);
    endTime = computed(() => this.stop()?.endTime ?? null);
    estimatedStartTime = computed(() => this.arrivalMovement()?.estimatedEndTime ?? null)
    estimatedEndTime = computed(() => this.stop()?.estimatedEndTime ?? null)

    originalPurposes = computed(() => {
        const originalActivities = this.vesselVisit()?.vesselActivities ?? [];

        if (this.stop()?.id != 0) {
            const originalActivity = originalActivities.find(activity => activity.activityType == VesselActivityType.Stop && activity.id == this.stop()?.id)
            return originalActivity?.purposes ?? [];
        }
        return [];
    })

    serviceApplicationsId = computed(() => this.stop()?.serviceApplications?.flatMap(sa => !sa.isDeleted ? sa.applicationTypeId : []) ?? []);

    arrivalMovementIsValid = computed(() => {
        const arrivalMovement = this.arrivalMovement();
        const vesselActivities = this.vesselActivities() ?? [];
        const arrivalMovementIndex = this.arrivalMovementIndex()
        if (!arrivalMovement || arrivalMovementIndex == undefined) return false;
        return VesselActivityHelper.movementIsValid(arrivalMovement, arrivalMovementIndex, vesselActivities)
    })
    departureMovementIsValid = computed(() => {
        const departureMovement = this.departureMovement();
        const departureIndex = this.departureMovementIndex();
        const vesselActivities = this.vesselActivities() ?? [];
        if (!departureMovement || departureIndex == undefined) return false;
        return VesselActivityHelper.movementIsValid(departureMovement, departureIndex, vesselActivities)
    })
    allFieldsValid = computed(() => {
        const stop = this.stop();
        const stopIndex = this.stopIndex();
        const vesselActivities = this.vesselActivities() ?? [];
        const arrivalValid = this.arrivalMovementIsValid();
        const departureValid = this.departureMovementIsValid();

        if (!stop || !stopIndex || !arrivalValid || !departureValid) return false;

        return VesselActivityHelper.stopIsValid(stop, stopIndex, vesselActivities) && this.allArrivalCargoesValid() && this.allDepartureCargoesValid();
    })

    constructor(private store: Store<any>) {
        effect(() => {
            const allFieldsValid = this.allFieldsValid();
            if (this.typedIndicesForEditingVesselActivities().length > 0) {
                this.allFieldsValidChange.emit(allFieldsValid);
            }
        })
    }

    toggleShowArrivalCargo() {
        this.showCargoState().showArrivalCargo.update(show => !show)
    }

    toggleShowDepartureCargo() {
        this.showCargoState().showDepartureCargo.update(show => !show)
    }

    onTerminalChange() {
        this.state().selectedBerth.set(undefined);
        this.onLocationChange();
    }

    onLocationChange() {
        const vesselActivities = this.vesselActivities() ?? [];
        const selectedLocation = this.state().selectedBerth() ?? this.state().selectedTerminal();
        const updatedActivities = VesselActivityHelper.updateVesselActivitiesOnLocationChange(vesselActivities, selectedLocation, this.arrivalMovementIndex(), this.stopIndex(), this.departureMovementIndex())
        this.store.dispatch(UpdateVesselActivitiesBeingEdited({ vesselActivities: updatedActivities }));
    }

    onServiceApplicationsIdChange(updatedIds: number[]) {
        const stopId = this.stop()?.id;
        const originalServiceApplications = this.vesselVisit()
            ?.vesselActivities
            ?.find(va => va.id === stopId && va.activityType === VesselActivityType.Stop)
            ?.serviceApplications ?? [];

        const updatedServiceApplications: IServiceApplication[] = updatedIds.map(id => {
            const isAdded = !originalServiceApplications.some(sa => sa.applicationTypeId === id);
            return {
                id: 0,
                stopId,
                applicationTypeId: id,
                isAdded
            } as IServiceApplication;
        });

        const removedApplications = originalServiceApplications.filter(
            osa => !updatedIds.includes(osa.applicationTypeId)
        );

        removedApplications.forEach(sa =>
            updatedServiceApplications.push({
                ...sa,
                isDeleted: true
            })
        );

        this.updateStop("serviceApplications", updatedServiceApplications)
    }

    onPurposesChange() {
        const stopIndex = this.stopIndex();
        if (!stopIndex) {
            return;
        }

        const newPurposes = this.originalPurposes()?.map(purpose => {
            if (!this.state().selectedPurposesId()?.some(id => id == purpose.id)) {
                return {
                    ...purpose,
                    isDeleted: true
                } as IVesselActivityPurpose;
            } else {
                return purpose;
            }
        }) ?? [];

        this.state().selectedPurposesId()?.map(id => {
            if (!this.originalPurposes()?.some(purpose => purpose.id == id)) {
                newPurposes.push({
                    id: 0,
                    purposeId: id,
                    stopId: this.stop()?.id,
                    isAdded: true
                } as IVesselActivityPurpose)
            }
        })

        const updatedActivity = {
            ...this.stop(),
            purposes: newPurposes
        } as IVesselStop;
        this._updateActivity(updatedActivity, stopIndex)
    }

    onAddArrivalCargo() {
        const newCargo = {
            id: 0,
            isAdded: true,
            stopId: this.arrivalMovement()?.id
        } as ICargo;
        this.state().arrivalCargoes.update(cargoes => [...cargoes, newCargo])
        this.allArrivalCargoesValid.set(false);
    }
    onArrivalCargoChange(newCargo: ICargo, index: number) {
        let updatedCargoes = this.state().arrivalCargoes();
        updatedCargoes = this._updateCargoes(newCargo, index, updatedCargoes);
        this.state().arrivalCargoes.set(updatedCargoes);

        const allCargoIsValid = this._validateAllCargoes(updatedCargoes);
        this.allArrivalCargoesValid.set(allCargoIsValid);

        if (!allCargoIsValid) return;

        this.updateArrivalMovement('cargoes', updatedCargoes);
    }
    onAddDepartureCargo() {
        const newCargo = {
            id: 0,
            isAdded: true,
            stopId: this.departureMovement()?.id
        } as ICargo;
        this.state().departureCargoes.update(cargoes => [...cargoes, newCargo])
        this.allDepartureCargoesValid.set(false);
    }
    onDepartureCargoChange(newCargo: ICargo, index: number) {
        let updatedCargoes = this.state().departureCargoes();
        updatedCargoes = this._updateCargoes(newCargo, index, updatedCargoes);
        this.state().departureCargoes.set(updatedCargoes);

        const allCargoIsValid = this._validateAllCargoes(updatedCargoes);
        this.allDepartureCargoesValid.set(allCargoIsValid);

        if (!allCargoIsValid) return;

        this.updateDepartureMovement('cargoes', updatedCargoes);
    }

    // onWithdraw() {
    //     let vesselActivities = this.vesselActivities() ?? [];

    //     if (this.stopIndex) {
    //         const updatedStop = {
    //             ...this.stop(),
    //             isDeleted: true
    //         } as IVesselStop;
    //         vesselActivities = VesselActivityHelper.updateVesselActivityInArray(updatedStop, vesselActivities, this.stopIndex);
    //     }

    //     if (this.arrivalMovementIndex != undefined) {
    //         const updatedArrivalMovement = {
    //             ...this.arrivalMovement(),
    //             isDeleted: true
    //         } as IVesselMovement;
    //         vesselActivities = VesselActivityHelper.updateVesselActivityInArray(updatedArrivalMovement, vesselActivities, this.arrivalMovementIndex);
    //     }
    //     this.store.dispatch(UpdateVesselActivitiesBeingEdited({ vesselActivities: vesselActivities }));
    // }

    // onDeletedOrganizationFromChild(organization: IVesselStopOrganization) {
    //     const updatedOrgs = this.organizations().filter(org => org.organization.id !== organization.organization.id);
    //     this.updateStopField('organizations', updatedOrgs);
    // }

    // onAssignedOrganizationFromChild(organization: IVesselStopOrganization) {
    //     const updatedOrgs = [...this.organizations(), organization];
    //     this.updateStopField('organizations', updatedOrgs);
    // }

    arrivalMovementFieldIsValid<K extends keyof IVesselMovement>(key: K): boolean {
        const arrivalMovement = this.arrivalMovement();
        if (!arrivalMovement) return false;

        const arrivalMovementIndex = this.arrivalMovementIndex();
        if (key === 'estimatedEndTime' || key === 'endTime') {
            const vesselActivities = this.vesselActivities();
            const previousStopIndex = arrivalMovementIndex && arrivalMovementIndex > 0
                ? arrivalMovementIndex - 1
                : null;

            const previousStop = vesselActivities && previousStopIndex !== null
                ? vesselActivities[previousStopIndex]
                : null;

            return VesselActivityHelper.movementFieldIsValid(key, arrivalMovement, previousStop, this.stop());
        }

        return VesselActivityHelper.movementFieldIsValid(key, arrivalMovement);
    }

    departureMovementFieldIsValid<K extends keyof IVesselMovement>(key: K) {
        const departureMovement = this.departureMovement();
        return departureMovement ? VesselActivityHelper.movementFieldIsValid(key, departureMovement) : false;
    }

    stopFieldIsValid<K extends keyof IVesselStop>(key: K) {
        const stop = this.stop();
        if (!stop) return false;

        const arrivalMovement = this.arrivalMovement();
        const departureMovement = this.departureMovement();

        return VesselActivityHelper.stopFieldIsValid(key, stop, arrivalMovement, departureMovement);
    }

    updateStop<K extends keyof IVesselStop>(key: K, value: IVesselStop[K]) {
        const stopIndex = this.stopIndex();
        const updatedStop = this._updateStopField(key, value);
        if (updatedStop && stopIndex) {
            this._updateActivity(updatedStop, stopIndex);
        }
    }

    updateArrivalMovement<K extends keyof IVesselMovement>(key: K, value: IVesselMovement[K]) {
        const arrivalMovement = this.arrivalMovement();
        const arrivalMovementIndex = this.arrivalMovementIndex();

        let updatedMovement: IVesselMovement | null = null;
        if (arrivalMovement) {
            updatedMovement = this._updateMovementField(key, value, arrivalMovement);
        }

        if (arrivalMovementIndex == undefined || !updatedMovement) {
            return;
        }

        this._updateMovement(updatedMovement, arrivalMovementIndex)
    }

    updateDepartureMovement<K extends keyof IVesselMovement>(key: K, value: IVesselMovement[K]) {
        const departureMovement = this.departureMovement();
        const departureMovementIndex = this.departureMovementIndex();
        let updatedMovement: IVesselMovement | null = null;
        if (departureMovement) {
            updatedMovement = this._updateMovementField(key, value, departureMovement);
        }

        if (!departureMovementIndex || !updatedMovement) {
            return;
        }

        this._updateMovement(updatedMovement, departureMovementIndex)
    }
    
    private _updateCargoes(newCargo: ICargo, index: number, cargoes: ICargo[]) {
        if (newCargo.isAdded && newCargo.isDeleted) {
            return cargoes.filter((_, i) => i !== index);
        } else {
            return cargoes.map((cargo, i) => i === index ? newCargo : cargo);
        }
    }

    private _validateAllCargoes(cargoes: ICargo[]) {
        return cargoes.every(cargo => CargoHelper.cargoIsValid(cargo));
    }

    private _updateActivity(newActivity: IVesselActivity, index: number) {
        const vesselActivities = this.vesselActivities() ?? [];
        const updatedVesselActivities = VesselActivityHelper.updateVesselActivityInArray(newActivity, vesselActivities, index);
        this.store.dispatch(UpdateVesselActivitiesBeingEdited({ vesselActivities: updatedVesselActivities }));
    }

    private _updateMovement(updatedMovement: IVesselMovement, index: number) {
        this._updateActivity(updatedMovement, index);
    }

    private _updateStopField<K extends keyof IVesselStop>(key: K, value: IVesselStop[K]) {
        const stop = this.stop();
        if (stop) {
            const updatedStop: IVesselStop = {
                ...stop,
                [key]: value
            };
            return updatedStop;
        }
        return null;
    }

    private _updateMovementField<K extends keyof IVesselMovement>(key: K, value: IVesselMovement[K], movement: IVesselMovement) {
        const updatedMovement: IVesselMovement = {
            ...movement,
            [key]: value
        };
        return updatedMovement;
    }
}